"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getBackendUrl } from '@/lib/utils/imageUrls';

export type PhotosphereQuality = 'light' | 'medium' | 'heavy';

const PHOTOSPHERE_QUALITY_KEY = 'eorguessr_photosphere_quality';
const DEFAULT_QUALITY: PhotosphereQuality = 'medium';

interface PhotosphereQualityContextType {
    quality: PhotosphereQuality;
    setQuality: (quality: PhotosphereQuality) => void;
    getQualitySuffix: () => string;
    getPhotosphereUrl: (photosphereId: string) => string;
    isLoaded: boolean;
}

const PhotosphereQualityContext = createContext<PhotosphereQualityContextType | undefined>(undefined);

interface PhotosphereQualityProviderProps {
    children: ReactNode;
}

export const PhotosphereQualityProvider: React.FC<PhotosphereQualityProviderProps> = ({ children }) => {
    const [quality, setQualityState] = useState<PhotosphereQuality>(DEFAULT_QUALITY);
    const [isLoaded, setIsLoaded] = useState(false);

    // Load quality from localStorage on mount
    useEffect(() => {
        try {
            const savedQuality = localStorage.getItem(PHOTOSPHERE_QUALITY_KEY);
            if (savedQuality && ['light', 'medium', 'heavy'].includes(savedQuality)) {
                setQualityState(savedQuality as PhotosphereQuality);
            }
        } catch (error) {
            console.warn('Failed to load photosphere quality from localStorage:', error);
        }
        setIsLoaded(true);
    }, []);

    // Update quality and save to localStorage
    const setQuality = (newQuality: PhotosphereQuality) => {
        console.log('Photosphere quality changed to:', newQuality); // Debug log
        setQualityState(newQuality);
        try {
            localStorage.setItem(PHOTOSPHERE_QUALITY_KEY, newQuality);
        } catch (error) {
            console.warn('Failed to save photosphere quality to localStorage:', error);
        }
    };

    // Get the URL suffix for the selected quality
    const getQualitySuffix = () => {
        return `panorama_${quality}.webp`;
    };

    // Get the full URL for a photosphere with the selected quality
    const getPhotosphereUrl = (photosphereId: string) => {
        const backendUrl = getBackendUrl();
        return `${backendUrl}/images/photospheres/${photosphereId}/${getQualitySuffix()}`;
    };

    const value: PhotosphereQualityContextType = {
        quality,
        setQuality,
        getQualitySuffix,
        getPhotosphereUrl,
        isLoaded,
    };

    return (
        <PhotosphereQualityContext.Provider value={value}>
            {children}
        </PhotosphereQualityContext.Provider>
    );
};

export const usePhotosphereQuality = (): PhotosphereQualityContextType => {
    const context = useContext(PhotosphereQualityContext);
    if (context === undefined) {
        throw new Error('usePhotosphereQuality must be used within a PhotosphereQualityProvider');
    }
    return context;
};